import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './state/auth.tsx';
import { startSyncScheduler } from './lib/sync.ts';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import Verify from './pages/Verify.tsx';
import Forgot from './pages/Forgot.tsx';
import Reset from './pages/Reset.tsx';
import Cards from './pages/Cards.tsx';
import { Onboarding } from './components/Onboarding.tsx';

// Split off the screens that carry the heavy dependencies: the card view pulls in the barcode
// renderer, the editor pulls in the scanner. Neither should delay the card list.
const Card = lazy(() => import('./pages/Card.tsx'));
const EditCard = lazy(() => import('./pages/EditCard.tsx'));
const Settings = lazy(() => import('./pages/Settings.tsx'));
const Manage = lazy(() => import('./pages/Manage.tsx'));
const ManageCard = lazy(() => import('./pages/ManageCard.tsx'));

/**
 * Until sign-in exists, nobody is sent to a login screen: a device without a session gets a guest
 * account, created by itself. Offline on the very first open there is nothing to create it with,
 * so it waits for the network and tries again.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status, startGuest } = useAuth();
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    if (status !== 'anonymous') return;
    const attempt = () => void startGuest().then(() => setOffline(false), () => setOffline(true));
    attempt();
    window.addEventListener('online', attempt);
    return () => window.removeEventListener('online', attempt);
  }, [status, startGuest]);

  if (status !== 'authenticated') {
    return (
      <div className="content"><div className="empty">
        <h2>{offline ? 'Serve la rete, solo la prima volta' : 'Preparo il tuo portafoglio…'}</h2>
        {offline && <p>Collegati a internet: da quel momento Klarnus funziona anche offline.</p>}
      </div></div>
    );
  }
  return <>{children}</>;
}

function AuthedOnly({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  return status === 'authenticated' ? <>{children}</> : null;
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  return status === 'authenticated' ? <Navigate to="/" replace /> : <>{children}</>;
}

/** Storage persistence is requested once the user has something worth keeping. */
function PersistStorage() {
  const { status } = useAuth();
  useEffect(() => {
    if (status !== 'authenticated') return;
    void navigator.storage?.persist?.().catch(() => {});
  }, [status]);
  return null;
}

function SyncScheduler() {
  const { status } = useAuth();
  useEffect(() => {
    if (status !== 'authenticated') return;
    return startSyncScheduler();
  }, [status]);
  return null;
}

/** Prompts once a new service worker is waiting, rather than swapping the app mid-checkout. */
function UpdateBanner() {
  const [waiting, setWaiting] = useState<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('virtual:pwa-register').then(({ registerSW }) => {
      if (cancelled) return;
      const update = registerSW({
        immediate: true,
        onNeedRefresh() {
          setWaiting(() => () => update(true));
        },
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!waiting) return null;
  return (
    <div className="alert info" style={{ position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 80 }}>
      <div className="spread">
        <span>C’è una nuova versione.</span>
        <button className="btn" style={{ minHeight: 36, padding: '6px 12px' }} onClick={waiting}>Aggiorna</button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PersistStorage />
        <SyncScheduler />
        <div className="app">
          <Suspense fallback={<div className="content"><p className="muted">Un attimo…</p></div>}>
            <Routes>
              <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
              <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="/verify" element={<Verify />} />
              <Route path="/" element={<RequireAuth><Cards /></RequireAuth>} />
              <Route path="/card/:id" element={<RequireAuth><Card /></RequireAuth>} />
              <Route path="/add" element={<RequireAuth><EditCard /></RequireAuth>} />
              <Route path="/edit/:id" element={<RequireAuth><EditCard /></RequireAuth>} />
              <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
              <Route path="/manage" element={<RequireAuth><Manage /></RequireAuth>} />
              <Route path="/manage/:id" element={<RequireAuth><ManageCard /></RequireAuth>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
        <UpdateBanner />
        <AuthedOnly><Onboarding /></AuthedOnly>
      </AuthProvider>
    </BrowserRouter>
  );
}
